import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Typography, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { MonthPicker } from 'components/ui/MonthPicker';
import { Formik } from 'formik';
import clsx from 'clsx';
import { MODAL_TYPES } from 'const/ModalContainer';
import { openModal } from 'modules/ModalContainer/actions';
import { showActionConfirmationModal } from 'components/InvoiceSearch/modals/InfoModal/showActionConfirmationModal';
import {
  getExcoInfoModalDataRequest,
  resetExcoInfoModalData,
  sendExcoInfoRequest,
  sendExcoInfoByEmployeeRequest,
  setSalaryModalVisibility,
  getCheckPendingChanges,
  getEmployeeExcoInfoModalDataRequest,
} from 'modules/SalaryModule/SalaryModal/actions';
import {
  selectExcoInfoModalData,
  selectExcoInfoModalDataLastFinalizedDate,
  selectExcoInfoModalDataStatus,
  selectModalVisibility,
  selectModalEmployee,
  selectExcoInfoModalDataLastDeclarationPeriod,
} from 'modules/SalaryModule/SalaryModal/selectors';
import { useSimpleMedia } from 'hooks/useMedia';
import {
  DATE_FORMAT_DOTTED,
  STATUS_LOADING,
  STATUS_NOT_REQUESTED,
  STATUS_SUCCESS,
} from 'const';
import { Spinner } from 'components/Spinner';
import { selectUserCurrentCompany } from 'modules/loggedUserInfo/selectors';
import { getOrderNumberForSendInfoStatistics } from 'helpers/sendInfoModal';
import {
  selectRewardsActiveMonth,
  selectEmployeeTransformRewards,
} from 'modules/SalaryModule/RewardsTable/selectors';
import { openSuccessNotification } from 'helpers/notifications';
import {
  EMPLOYEE_MODAL_INFO,
  EXCO_INFO_MODAL_DATA_LABELS,
} from 'const/translations/SalaryMainPage';
import { Button } from 'components/ui';
import Styles from './InfoModal.module.css';

const SCREEN_WIDTH_BREAKPOINT_VALUE = 360;
const SCREEN_WIDTH_BREAKPOINT_MEDIA = `(max-width: ${SCREEN_WIDTH_BREAKPOINT_VALUE}px)`;

export const InfoModal = memo(() => {
  const isVisible = useSelector(selectModalVisibility);
  const modalData = useSelector(selectExcoInfoModalData);
  const modalDataStatus = useSelector(selectExcoInfoModalDataStatus);
  const modalEmployee = useSelector(selectModalEmployee);
  const employeesRewards = useSelector(selectEmployeeTransformRewards);
  const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);
  const dispatch = useDispatch();
  const onClose = useCallback(() => {
    dispatch(setSalaryModalVisibility(false));
  }, [dispatch]);
  const lastFinalizedDate = useSelector(
    selectExcoInfoModalDataLastFinalizedDate
  );
  const rewardsActiveMonth = useSelector(selectRewardsActiveMonth);
  const lastDeclarationPeriod = useSelector(
    selectExcoInfoModalDataLastDeclarationPeriod
  );

  const currentEmployee =
    modalEmployee.id &&
    employeesRewards.find((employee) => employee.id === modalEmployee.id);

  const [activeMonth, setActiveMonth] = useState(
    moment(rewardsActiveMonth).startOf('month')
  );

  const { id: companyId } = useSelector(selectUserCurrentCompany);

  const { totalRewards } = modalData;

  useEffect(() => {
    setActiveMonth(moment(rewardsActiveMonth));
  }, [rewardsActiveMonth]);

  useEffect(() => {
    if (!isVisible) {
      dispatch(resetExcoInfoModalData());
      return;
    }

    if (modalEmployee.id) {
      dispatch(
        getEmployeeExcoInfoModalDataRequest({
          companyId,
          employeeId: modalEmployee.id,
        })
      );
      return;
    }

    dispatch(getExcoInfoModalDataRequest({ companyId }));
  }, [dispatch, isVisible, companyId, modalEmployee.id]);

  useEffect(() => {
    if (isVisible) {
      const month = moment(activeMonth).month() + 1;
      const year = moment(activeMonth).year();

      dispatch(getCheckPendingChanges({ companyId, month, year }));
      return;
    }
    dispatch(resetExcoInfoModalData());
  }, [dispatch, isVisible, companyId, activeMonth]);

  const openNotification = useCallback(() => {
    openSuccessNotification({
      message: EMPLOYEE_MODAL_INFO.SUCCESS_MESSAGE,
      color: 'darkGreen',
      wide: true,
    });
  }, []);

  const handleShowContactHRModal = useCallback(() => {
    onClose();
    dispatch(openModal({ type: MODAL_TYPES.contactHR }));
  }, [dispatch, onClose]);

  const isThereNothingToFinalize = useMemo(
    () => Object.values(modalData).every((value) => !value),
    [modalData]
  );

  const onSubmit = useCallback(
    ({ comment }) => {
      if (isThereNothingToFinalize) {
        showActionConfirmationModal({
          handleOk: () => {
            dispatch(
              modalEmployee.id
                ? sendExcoInfoByEmployeeRequest({
                    onSuccess: openNotification,
                    date: moment(activeMonth),
                    comment,
                    companyId,
                    employeeId: modalEmployee.id,
                  })
                : sendExcoInfoRequest({
                    onSuccess: openNotification,
                    date: moment(activeMonth),
                    comment,
                    companyId,
                  })
            );
            onClose();
          },
          confirmationText: EMPLOYEE_MODAL_INFO.EMPTY_DECLARATION,
        });

        return;
      }

      dispatch(
        modalEmployee.id
          ? sendExcoInfoByEmployeeRequest({
              onSuccess: openNotification,
              date: moment(activeMonth),
              comment,
              companyId,
              employeeId: modalEmployee.id,
            })
          : sendExcoInfoRequest({
              onSuccess: openNotification,
              date: moment(activeMonth),
              comment,
              companyId,
            })
      );
      onClose();
    },
    [
      isThereNothingToFinalize,
      dispatch,
      modalEmployee.id,
      openNotification,
      activeMonth,
      companyId,
      onClose,
    ]
  );

  const isPeriodDeclared = useCallback(
    (lastDeclaration) =>
      lastDeclaration?.year &&
      lastDeclaration?.month &&
      moment(`${lastDeclaration.year}.${lastDeclaration.month}`, 'YYYY.MM')
        .endOf('month')
        .isAfter(moment(activeMonth)),
    [activeMonth]
  );

  const isDeclarationDisabled = modalEmployee.id
    ? isPeriodDeclared(currentEmployee.lastDeclarationPeriod)
    : modalDataStatus !== STATUS_SUCCESS ||
      isPeriodDeclared(lastDeclarationPeriod);

  return (
    <Modal
      open={isVisible}
      footer={null}
      onCancel={onClose}
      className={Styles.wrapper}
      width={isMobileVersion ? '100%' : '422px'}
      destroyOnHidden
    >
      <Typography.Title level={4} className={Styles.textTitle}>
        {modalEmployee.id
          ? `${EMPLOYEE_MODAL_INFO.TITLE_FOR_EMPLOYEE} ${modalEmployee.name}`
          : EMPLOYEE_MODAL_INFO.TITLE}
      </Typography.Title>
      {lastFinalizedDate && (
        <Typography.Paragraph className={Styles.textSubtitle}>
          {`${EMPLOYEE_MODAL_INFO.SUBTITLE}  ${moment(lastFinalizedDate).format(
            DATE_FORMAT_DOTTED
          )}`}
        </Typography.Paragraph>
      )}
      {!modalEmployee.id && (
        <div>
          {EMPLOYEE_MODAL_INFO.WARNING}{' '}
          <span className={Styles.linkToHr} onClick={handleShowContactHRModal}>
            {EMPLOYEE_MODAL_INFO.CONTACT_HR}
          </span>
        </div>
      )}

      <div className={Styles.blockWrapper}>
        <Typography.Text className={Styles.textLabel}>
          {EMPLOYEE_MODAL_INFO.LABELS.MONTH_PICKER}
        </Typography.Text>
        <MonthPicker value={moment(activeMonth)} setValue={setActiveMonth} />
      </div>
      <div className={Styles.blockWrapper}>
        <Typography.Text className={Styles.textLabel}>
          {EMPLOYEE_MODAL_INFO.LABELS.STATISTICS}
        </Typography.Text>
        <div className={Styles.statisticsWrapper}>
          {[STATUS_NOT_REQUESTED, STATUS_LOADING].includes(modalDataStatus) ? (
            <Spinner className={Styles.spinnerWrapper} />
          ) : (
            Object.entries({ totalRewards }).map(([label, value]) => (
              <div
                key={label}
                style={{ order: getOrderNumberForSendInfoStatistics(label) }}
                className={Styles.textStatistics}
              >
                <span className={Styles.statisticsNumber}>{value}</span>
                <span className={Styles.statisticsText}>
                  {EXCO_INFO_MODAL_DATA_LABELS[label]}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      <Formik
        initialValues={{ comment: '' }}
        onSubmit={onSubmit}
        validate={(values) => {
          const errors = {};
          if (values.comment.length > 200) {
            errors.comment = '200 max symbols';
          }
          return errors;
        }}
      >
        {({ values, handleChange, handleSubmit, errors }) => (
          <form onSubmit={handleSubmit}>
            <textarea
              name="comment"
              className={clsx(Styles.modalInput, {
                [Styles.errorModalInput]: errors.comment,
              })}
              value={values.comment}
              onChange={handleChange}
              placeholder={EMPLOYEE_MODAL_INFO.TEXT_AREA_PLACEHOLDER}
            />
            <div className={Styles.buttonsWrapper}>
              <Button
                variant="secondary"
                onClick={onClose}
                className={Styles.button}
              >
                {EMPLOYEE_MODAL_INFO.CANCEL_BUTTON}
              </Button>

              {isPeriodDeclared ? (
                <Tooltip
                  placement="topLeft"
                  arrow={{ pointAtCenter: true }}
                  title="Opération impossible, vous avez déjà validé les données de paie pour cette période"
                  classNames={{ root: Styles.toolTip }}
                  trigger="hover"
                >
                  <div>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isDeclarationDisabled}
                      className={Styles.button}
                    >
                      {EMPLOYEE_MODAL_INFO.SUBMIT_BUTTON}
                    </Button>
                  </div>
                </Tooltip>
              ) : (
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isDeclarationDisabled}
                  className={Styles.button}
                >
                  {EMPLOYEE_MODAL_INFO.SUBMIT_BUTTON}
                </Button>
              )}
            </div>
          </form>
        )}
      </Formik>
    </Modal>
  );
});
